import type { EquipmentItem } from '@sb/integrations/types';
import type { Step as RcStep } from '@sb/remote-control/types';
import { getStepKindInfo } from '@sb/remote-control/util/stepKindInfo';
import type { Space, StepKind } from '@sb/routine-runner';
import { DEFAULT_SPEED_PROFILE } from '@sb/routine-runner/speed-profile/SpeedProfile';

import {
  type Diagnostic,
  type RoutineView,
  type ValidationContext,
  type ValidationRule,
} from '../../types';
import { ActionRequiredError } from '../../util/errors';
import { createStepRule, diag } from '../../util/ruleHelpers';
import { getRunnerArgsSchema } from '../../util/runnerSchemas';

function ensureArgsWithArgumentKind(
  stepKind: StepKind,
  args: unknown,
): RcStep.Arguments {
  const isObj =
    args != null && typeof args === 'object' && !Array.isArray(args);

  const hasArgumentKind =
    isObj && (args as Record<string, unknown>)?.argumentKind != null;

  if (isObj && hasArgumentKind) return args as RcStep.Arguments;
  if (isObj)
    return { argumentKind: stepKind, ...(args as object) } as RcStep.Arguments;

  return { argumentKind: stepKind } as RcStep.Arguments;
}

function findStepNode(
  routine: RoutineView,
  stepId: string,
): { id: string; stepKind: StepKind | string; children: string[] } | null {
  const s = routine.steps.find((x) => x.id === stepId);

  return s
    ? { id: s.id, stepKind: s.stepKind, children: s.children ?? [] }
    : null;
}

export function createCanonicalStepRule(opts: {
  id: string;
  stepKind: StepKind | string;
  validate: (args: {
    stepId: string;
    stepKind: StepKind | string;
    parents: Array<{ id: string; stepKind: StepKind | string }>;
    children: Array<{ id: string; stepKind: StepKind | string }>;
    args: RcStep.Arguments;
    routine: RoutineView;
    ctx: ValidationContext;
  }) => void;
}): (args: {
  routine: RoutineView;
  ctx: ValidationContext;
  stepId: string;
}) => Diagnostic[] {
  return ({ routine, ctx, stepId }) => {
    const node = findStepNode(routine, stepId);
    if (!node) return [];

    type RoutineViewWithDerived = RoutineView & {
      derived?: {
        parentsById?: Record<
          string,
          Array<{ id: string; stepKind: StepKind | string }>
        >;
      };
    };

    const parents =
      (routine as RoutineViewWithDerived).derived?.parentsById?.[stepId] ?? [];

    const children =
      (node.children ?? [])
        .map((cid) => findStepNode(routine, cid))
        .filter((x): x is NonNullable<typeof x> => x != null)
        .map((c) => ({ id: c.id, stepKind: c.stepKind })) ?? [];

    const stepCfg = routine.stepConfigurations[stepId];

    const ensuredArgs = ensureArgsWithArgumentKind(
      node.stepKind as StepKind,
      stepCfg?.args as unknown,
    );

    const diagnostics: Diagnostic[] = [];

    try {
      opts.validate({
        stepId,
        stepKind: node.stepKind,
        parents,
        children,
        args: ensuredArgs,
        routine,
        ctx,
      });
    } catch (err) {
      const ar = (
        err as
          | {
              actionRequired?: {
                kind: string;
                message?: string;
                fieldId?: string;
              };
            }
          | undefined
      )?.actionRequired;

      if (ar) {
        diagnostics.push(
          diag({
            code: `ui.${ar.kind}`,
            message: ar.message ?? 'Invalid configuration',
            fieldId: ar.fieldId,
            stepId,
            sourceRule: opts.id,
          }),
        );

        return diagnostics;
      }

      if (err instanceof Error) {
        diagnostics.push(
          diag({
            code: 'ui.invalidConfiguration',
            message: err.message,
            stepId,
            sourceRule: opts.id,
          }),
        );

        return diagnostics;
      }

      diagnostics.push(
        diag({
          code: 'ui.invalidConfiguration',
          message: `Configuration is not valid (${String(err)})`,
          stepId,
          sourceRule: opts.id,
        }),
      );

      return diagnostics;
    }

    return diagnostics;
  };
}

export function buildStepRule({
  id,
  stepKind,
  runner,
}: {
  id: string;
  stepKind: string;
  runner: (args: {
    routine: RoutineView;
    ctx: ValidationContext;
    stepId: string;
  }) => Diagnostic[];
}) {
  return createStepRule({
    id,
    stepKinds: [stepKind],
    run: runner,
  });
}

export type CanonicalValidateArgs = {
  stepId: string;
  stepKind: StepKind | string;
  parents: Array<{ id: string; stepKind: StepKind | string }>;
  children: Array<{ id: string; stepKind: StepKind | string }>;
  args: RcStep.Arguments;
  routine: RoutineView;
  ctx: ValidationContext;
};

export abstract class CanonicalStepRuleBase {
  public readonly id: string;

  public readonly stepKind: string;

  protected constructor(id: string, stepKind: string) {
    this.id = id;
    this.stepKind = stepKind;
  }

  /**
   * Ensure the provided args match this.stepKind.
   * Throws TypeError if the argument kind does not match.
   *
   * @throws {TypeError} When args.argumentKind !== this.stepKind
   */
  protected ensureArgumentKind(
    args: { argumentKind?: string } | unknown,
  ): void {
    const argKind = (args as { argumentKind?: string } | undefined)
      ?.argumentKind;

    if (argKind !== this.stepKind) {
      throw new TypeError(`Expected argument kind ${this.stepKind}`);
    }
  }

  /**
   * Combine global and routine space items into a single array.
   *
   * @returns The combined space array.
   */
  protected getSpaceItems(
    routine: { space?: Space.Item[] } | undefined,
    ctx: { globalSpace?: Space.Item[] } | undefined,
  ): Space.Item[] {
    return [
      ...((ctx?.globalSpace ?? []) as Space.Item[]),
      ...((routine?.space ?? []) as Space.Item[]),
    ];
  }

  /**
   * Find equipment by id in the validation context.
   *
   * @returns {any|undefined} The equipment config if found
   */
  protected findEquipment(
    ctx: { equipment?: EquipmentItem[] } | undefined,
    id: string,
  ): EquipmentItem | undefined {
    return (ctx?.equipment ?? []).find((e) => e.id === id);
  }

  // Placeholder for a standardized "third check" step after subclass validation,
  // e.g. Zod schema conformance. Default is a no-op.
  // Subclasses can override if needed later.
  protected runnerSchemaValidate(args: CanonicalValidateArgs): void {
    const schema = getRunnerArgsSchema(this.stepKind);
    if (!schema) return;

    const originalArgs = (args.args ?? {}) as Record<string, unknown>;
    const { argumentKind: _argumentKind, ...rest } = originalArgs;

    // If we have the raw UI routine, try converting the UI args into runner-shape
    // using the remote-control step's toRoutineRunner. This avoids false positives
    // where UI shapes differ from runner shapes (e.g., SetIO, MoveArmToV2).
    let candidate: Record<string, unknown> = rest;

    try {
      const toRR = getStepKindInfo(this.stepKind as StepKind).toRoutineRunner;
      const hasRawUI = args.ctx?.rawRoutine != null;

      if (hasRawUI && typeof toRR === 'function') {
        const converted = toRR({
          // Pass minimal UI-shaped config; toRoutineRunner only reads stepKind/args
          stepConfiguration: {
            id: args.stepId,
            stepKind: this.stepKind as StepKind,
            args: originalArgs as unknown,
          } as any,
          stepData: { id: args.stepId } as any,
          equipment: args.ctx?.equipment ?? [],
          baseSpeedProfile: DEFAULT_SPEED_PROFILE,
          codeBlocks: [],
        });

        const convertedArgs = (converted as { args?: unknown })?.args;

        if (convertedArgs && typeof convertedArgs === 'object') {
          candidate = convertedArgs as Record<string, unknown>;
        }
      }
    } catch {
      // Best-effort conversion; fall back to UI args if conversion fails
    }

    // Validate only the keys present (treat missing keys as ok); prefer deep partial when available.
    const schemaAny = schema as unknown as {
      deepPartial?: () => typeof schema;
      partial?: () => typeof schema;
    };

    const partialSchema =
      schemaAny.deepPartial?.() ?? schemaAny.partial?.() ?? schema;

    const result = partialSchema.safeParse(candidate);
    if (result.success) return;

    const issue = result.error.issues[0];

    const fieldId =
      Array.isArray(issue?.path) && issue.path.length > 0
        ? String(issue.path[0])
        : undefined;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: issue?.message || 'Arguments are not valid for this step',
      fieldId,
    });
  }

  protected abstract validate(args: CanonicalValidateArgs): void;

  public toValidationRule(): ValidationRule {
    const runner = createCanonicalStepRule({
      id: this.id,
      stepKind: this.stepKind,
      validate: (args) => {
        // Validations occur in priority order:
        // 1) Ensure arguments are for the expected step kind
        this.ensureArgumentKind(args.args);
        // 2) Run subclass-provided validations
        this.validate(args);
        // 3) Validate against the runner schema
        this.runnerSchemaValidate(args);
      },
    });

    return buildStepRule({
      id: this.id,
      stepKind: this.stepKind,
      runner,
    });
  }
}
